export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
export const CONSENT_STORAGE_KEY = `lvm_consent_v${CONSENT_VERSION}`;
export const CONSENT_CHANGE_EVENT = 'lvm:consent-change';
export const CONSENT_OPEN_EVENT = 'lvm:consent-open';

const OPTIONAL_STORAGE_KEYS = [
  'lvm_tracking_fbc',
  'lvm_tracking_fbp',
  'lvm_tracking_ttclid',
  'lvm_tracking_ttp',
  'lvm_tracking_external_id',
  'lvm_tracking_identity'
];

const MARKETING_COOKIE_NAMES = [
  '_fbc',
  '_fbp',
  '_ttp',
  'ttclid',
  'lvm_tracking_external_id',
  'lvm_em',
  'lvm_ph',
  'lvm_fn',
  'lvm_ln',
  '_gcl_au'
];

const ANALYTICS_COOKIE_NAMES = [
  '_ga',
  '_gid',
  '_gat'
];

let hostConsentPreferences = null;
let successPageConsentOverride = null;
let volatileConsentPreferences = null;
let lastConsentStorageWriteSuccessful = true;

const isEmbedRuntime = () => {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search || '');
    return params.get('embed') === '1' || params.get('lvm_embed') === '1';
  } catch (error) {
    return false;
  }
};

const isSuccessPageEmbedReturnCandidate = () => {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search || '');
    return window.location.pathname === '/checkout/success'
      && Boolean(String(params.get('lvm_embed_token') || '').trim());
  } catch (error) {
    return false;
  }
};

const successReturnSession = () => {
  if (!isSuccessPageEmbedReturnCandidate()) return '';
  return String(new URLSearchParams(window.location.search).get('session_id') || '').slice(0, 256);
};

const pendingPreferences = () => ({
  version: CONSENT_VERSION,
  status: 'pending',
  necessary: true,
  analytics: false,
  marketing: false,
  decisionId: '',
  decidedAt: null,
  expiresAt: null,
  source: 'default'
});

const createDecisionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `consent_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

const normalizeStoredPreferences = (value) => {
  if (!value || typeof value !== 'object') return null;
  if (value.version !== CONSENT_VERSION || value.status !== 'decided') return null;
  if (value.necessary !== true) return null;
  if (typeof value.analytics !== 'boolean' || typeof value.marketing !== 'boolean') return null;
  if (!value.decisionId || !value.decidedAt) return null;

  const decidedAtMs = Date.parse(value.decidedAt);
  if (!Number.isFinite(decidedAtMs)) return null;
  const storedExpiresAtMs = Date.parse(value.expiresAt);
  const expiresAtMs = Number.isFinite(storedExpiresAtMs)
    ? storedExpiresAtMs
    : decidedAtMs + CONSENT_MAX_AGE_MS;
  if (expiresAtMs <= Date.now()) return null;

  return {
    version: CONSENT_VERSION,
    status: 'decided',
    necessary: true,
    analytics: value.analytics,
    marketing: value.marketing,
    decisionId: String(value.decisionId).slice(0, 120),
    decidedAt: new Date(decidedAtMs).toISOString(),
    expiresAt: new Date(expiresAtMs).toISOString(),
    source: String(value.source || 'stored').slice(0, 40),
    ...(value.source === 'withdraw' && value.marketing === false && value.withdrawalSessionId
      ? { withdrawalSessionId: String(value.withdrawalSessionId).slice(0, 256) } : {})
  };
};

// A rejection made on this return page remains authoritative while its binding
// loads, and after reload if the server withdrawal request has not finished.
// An unrelated older LVM preference still cannot override the host snapshot.
const getReturnWithdrawal = () => {
  const session = successReturnSession();
  if (!session) return null;
  let stored;
  try { stored = JSON.parse(window.localStorage.getItem(CONSENT_STORAGE_KEY) || 'null'); } catch (error) { /* storage unavailable */ }
  const choice = normalizeStoredPreferences(volatileConsentPreferences) || normalizeStoredPreferences(stored);
  return choice?.withdrawalSessionId === session
    ? { ...choice, withdrawn: true, browserTracking: false } : null;
};

export const getConsentPreferences = () => {
  if (typeof window === 'undefined') return pendingPreferences();

  const withdrawal = getReturnWithdrawal();
  if (withdrawal) return { ...successPageConsentOverride, ...withdrawal };
  if (successPageConsentOverride) return { ...successPageConsentOverride };

  // A raw token grants no authority, but it does identify a return whose HMAC
  // binding still needs server verification. Fail closed so a previous LVM
  // choice cannot initialize pixels during that verification race.
  if (isSuccessPageEmbedReturnCandidate()) {
    return {
      ...pendingPreferences(),
      source: 'embed_binding_pending',
      authority: 'pending_embed_binding',
      browserTracking: false
    };
  }

  // Embedded checkout has one authority: the verified parent bridge. Host
  // consent is deliberately memory-only and never mixed with LVM storage.
  if (hostConsentPreferences) return { ...hostConsentPreferences };
  if (isEmbedRuntime()) {
    return {
      ...pendingPreferences(),
      source: 'embed_host',
      authority: 'host',
      identifiers: {},
      browserTracking: false
    };
  }

  try {
    const stored = JSON.parse(window.localStorage.getItem(CONSENT_STORAGE_KEY) || 'null');
    return normalizeStoredPreferences(volatileConsentPreferences)
      || normalizeStoredPreferences(stored)
      || window.LVMTrackingNotice?.defaultPermission()
      || pendingPreferences();
  } catch (error) {
    return normalizeStoredPreferences(volatileConsentPreferences)
      || window.LVMTrackingNotice?.defaultPermission() || pendingPreferences();
  }
};

export const hasConsent = (category) => {
  if (category === 'necessary') return true;
  const preferences = getConsentPreferences();
  return (preferences.status === 'decided' || (preferences.status === 'notice' && preferences.basis === 'notice_opt_out'))
    && preferences[category] === true
    && preferences.browserTracking !== false;
};

const dispatchConsentChange = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: getConsentPreferences() }));
};

export const setSuccessPageConsentOverride = (preferences = {}) => {
  if (!preferences || typeof preferences !== 'object') return null;
  successPageConsentOverride = {
    ...preferences,
    ...getReturnWithdrawal(),
    version: CONSENT_VERSION,
    necessary: true,
    source: 'embed_order_snapshot',
    authority: 'order_snapshot',
    verifiedEmbedReturn: true
  };
  dispatchConsentChange();
  return { ...successPageConsentOverride };
};

export const clearSuccessPageConsentOverride = () => {
  if (!successPageConsentOverride) return;
  successPageConsentOverride = null;
  dispatchConsentChange();
};

const expireCookie = (name) => {
  if (typeof document === 'undefined') return;
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const domains = ['', host, host ? `.${host.replace(/^www\./, '')}` : ''].filter((value, index, list) => (
    list.indexOf(value) === index
  ));

  domains.forEach((domain) => {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;
  });
};

export const clearOptionalTrackingData = ({ analytics = true, marketing = true } = {}) => {
  if (typeof window === 'undefined') return;

  if (marketing) {
    window.LVMMatchingIdentity?.clear();
    OPTIONAL_STORAGE_KEYS.forEach((key) => {
      try { window.localStorage.removeItem(key); } catch (error) { /* storage unavailable */ }
      try { window.sessionStorage.removeItem(key); } catch (error) { /* storage unavailable */ }
    });
    MARKETING_COOKIE_NAMES.forEach(expireCookie);
  }
  if (analytics) ANALYTICS_COOKIE_NAMES.forEach(expireCookie);

  // Remove provider-created GA cookies that include a measurement suffix.
  if (analytics && typeof document !== 'undefined') {
    document.cookie.split(';').forEach((entry) => {
      const name = entry.split('=')[0].trim();
      if (name.startsWith('_ga_')) expireCookie(name);
    });
  }
  if (marketing && typeof document !== 'undefined') {
    document.cookie.split(';').forEach((entry) => {
      const name = entry.split('=')[0].trim();
      if (name.startsWith('_gcl_')) expireCookie(name);
    });
  }
};

export const setConsentPreferences = (categories = {}, source = 'preferences', withdrawalSessionId = successReturnSession()) => {
  const decidedAt = new Date();
  const preferences = {
    version: CONSENT_VERSION,
    status: 'decided',
    necessary: true,
    analytics: categories.analytics === true,
    marketing: categories.marketing === true,
    decisionId: createDecisionId(),
    decidedAt: decidedAt.toISOString(),
    expiresAt: new Date(decidedAt.getTime() + CONSENT_MAX_AGE_MS).toISOString(),
    source: String(source || 'preferences').slice(0, 40),
    ...(source === 'withdraw' && categories.marketing !== true && withdrawalSessionId
      ? { withdrawalSessionId: String(withdrawalSessionId).slice(0, 256) } : {})
  };

  if (typeof window !== 'undefined') {
    lastConsentStorageWriteSuccessful = true;
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences));
      volatileConsentPreferences = null;
    } catch (error) {
      // Preserve the decision for this page view so UI and provider gates use
      // the same state. It intentionally disappears when the page closes.
      volatileConsentPreferences = preferences;
      lastConsentStorageWriteSuccessful = false;
    }
  }

  clearOptionalTrackingData({
    analytics: !preferences.analytics,
    marketing: !preferences.marketing
  });
  dispatchConsentChange();
  return preferences;
};

export const wasConsentStorageWriteSuccessful = () => lastConsentStorageWriteSuccessful;

export const setHostConsentPreferences = ({ status, basis, categories = {}, decisionId = '', decidedAt = null, identifiers = {} } = {}) => {
  const isNotice = status === 'notice' && basis === 'notice_opt_out'
    && categories.marketing === true && categories.analytics === false;
  const isDecided = status === 'granted' || status === 'denied' || isNotice;
  const marketing = (status === 'granted' || isNotice) && categories.marketing === true;
  const analytics = status === 'granted' && categories.analytics === true;
  const normalizedDecidedAt = isDecided && decidedAt && Number.isFinite(Date.parse(decidedAt))
    ? new Date(decidedAt).toISOString()
    : null;

  hostConsentPreferences = {
    ...pendingPreferences(),
    status: isNotice ? 'notice' : (isDecided ? 'decided' : 'pending'),
    ...(isNotice ? { basis: 'notice_opt_out' } : {}),
    analytics,
    marketing,
    decisionId: isDecided ? String(decisionId || '').slice(0, 120) : '',
    decidedAt: normalizedDecidedAt,
    source: 'embed_host',
    authority: 'host',
    identifiers: marketing && identifiers && typeof identifiers === 'object'
      ? { ...identifiers }
      : {},
    // The host emits browser events. The iframe may pass this decision to
    // LVM server endpoints, but must not initialize Meta/TikTok/GA itself.
    browserTracking: false
  };

  if (!marketing || !analytics) {
    clearOptionalTrackingData({ analytics: !analytics, marketing: !marketing });
  }
  dispatchConsentChange();
  return { ...hostConsentPreferences };
};

export const clearHostConsentPreferences = () => {
  if (!hostConsentPreferences) return;
  hostConsentPreferences = null;
  dispatchConsentChange();
};

export const withdrawConsent = (checkoutSessionId = successReturnSession()) => {
  const preferences = setConsentPreferences({ analytics: false, marketing: false }, 'withdraw', checkoutSessionId);
  if (successPageConsentOverride) {
    successPageConsentOverride = {
      ...preferences, withdrawn: true, authority: 'order_snapshot',
      verifiedEmbedReturn: true, browserTracking: false
    };
    dispatchConsentChange();
  }
  return preferences;
};

export const openConsentPreferences = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
};

export const toTrackingConsentPayload = (preferences = getConsentPreferences()) => ({
  version: preferences.version,
  status: preferences.status,
  ...(preferences.status === 'notice' ? { basis: 'notice_opt_out' } : {}),
  necessary: true,
  analytics: preferences.analytics === true,
  marketing: preferences.marketing === true,
  decisionId: preferences.decisionId || '',
  decidedAt: preferences.decidedAt || null,
  ...(preferences.authority === 'host' ? { authority: 'host' } : {})
});
